import type { Brand, BrandColors, BrandId, LoginVideo } from './types'

// ── Lo que Unique Perfumes comparte con Tailored ─────────────────────────────
// Son constantes y no copias a propósito: el encargo fue «la misma tipografía y
// los mismos colores», y una copia pegada se queda atrás en silencio cuando una
// de las dos cambia. Si algún día tienen que separarse, se copia la constante
// dentro de la marca que se desvía y se acabó. El vídeo ya se separó así.

const JOST = {
  fontFamily: "'Jost', system-ui, sans-serif",
  fontHref: 'https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500&display=swap',
}

/** Monocromo derivado de #231f20. */
const MONOCROMO: BrandColors = {
  dark: '#231f20', bg: '#f6f5f3', primary: '#231f20',
  border: '#e5e3e0', muted: '#8a857f',
  aq: '#ece9e6', aq2: '#6b6560', bl: '#e4e1dd', bl2: '#4a4642',
  sg: '#eceae7', sg2: '#5f5a55', sd: '#e8e5e1', sd2: '#9a938c',
}

// El vídeo del acceso editorial es de cada marca: lo comparten el diseño y la
// paleta, no las imágenes.

const VIDEO_TAILORED: LoginVideo = {
  webm: '/tailored_login.webm',
  mp4: '/tailored_login.mp4',
  poster: '/360.png',
}

const VIDEO_UNIQ: LoginVideo = {
  webm: '/login-uniq-webm.webm',
  mp4: '/login-uniq-webm.mp4',
  // Pendiente: un fotograma del vídeo de Unique. Éste es el de Tailored, y es
  // lo que se ve con `prefers-reduced-motion`, no un detalle de carga.
  poster: '/360.png',
}

const AIRVAL: Brand = {
  id: 'airval',
  name: 'Airval · Factory Dashboard',
  chromeLabel: 'AIRVAL',
  logoOnDark: '/logo-white.svg',
  logoLogin: '/logo.svg',
  favicon: '/favicon.ico',
  fontFamily: "'Inter', system-ui, sans-serif",
  fontHref: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
  loginLayout: 'default',
  tagline: 'Factory Dashboard',
  colors: {
    dark: '#3b4559', bg: '#f0eee9', primary: '#6db8b2',
    border: '#e2ded8', muted: '#7a8299',
    aq: '#c5e4e0', aq2: '#6db8b2', bl: '#becae8', bl2: '#7090c8',
    sg: '#d0e4bb', sg2: '#5e9640', sd: '#d2c8be', sd2: '#9e8e82',
  },
}

const TAILORED: Brand = {
  id: 'tailored',
  name: 'Tailored · Factory Dashboard',
  chromeLabel: 'TAILORED',
  logoOnDark: '/logo_ICONO-PERFUME-CIRCULAR-02-02-e1726582750776.png',   // disco negro + botella blanca recortada; invert(1) por CSS en fondos oscuros
  logoLogin: '/tailored-logo.png',
  favicon: '/tailored_favicon.ico',
  invertLogoOnDark: true,
  ...JOST,
  loginLayout: 'editorial',
  loginVideo: VIDEO_TAILORED,
  tagline: 'WE BUILD BEAUTY BRANDS',
  colors: MONOCROMO,
}

const UNIQ: Brand = {
  id: 'uniq',
  name: 'Unique Perfumes · Factory Dashboard',
  chromeLabel: 'UNIQUE',
  // Un solo fichero para el cromo y para el login: el que hay es el logotipo
  // completo, con el texto debajo. En el sidebar se ve pequeño y el texto no se
  // lee, pero al lado va el rótulo «UNIQUE Factory», así que no se pierde nada.
  // El día que llegue una versión sólo del símbolo, es cambiar esta línea.
  logoOnDark: '/uniq-logo.png',
  invertLogoOnDark: true,   // el logotipo es negro
  logoLogin: '/uniq-logo.png',
  // PNG y no .ico: el navegador lo acepta igual y así la marca entra sin pedir
  // un fichero más. Si alguien prepara un .ico, se cambia aquí.
  favicon: '/uniq-logo.png',
  ...JOST,
  loginLayout: 'editorial',
  // Unique compra y vende, no fabrica. Ver `loginSubtitleKey`.
  loginSubtitleKey: 'login.subtitleComercial',
  loginVideo: VIDEO_UNIQ,
  tagline: 'WE CREATE UNIQUE FRAGRANCES',
  colors: MONOCROMO,
}

export const BRANDS: Record<BrandId, Brand> = { airval: AIRVAL, tailored: TAILORED, uniq: UNIQ }

/** Dominio de producción de cada marca que no es la de por defecto.
 *
 *  Una fila por marca: añadir la siguiente es añadir una fila, no otro `if`.
 *  El resto de hosts —localhost incluido— se queda en Airval, y en dev se
 *  previsualiza cualquiera con ?brand= o con el switcher. */
const HOSTS: readonly { readonly host: string; readonly brand: Brand }[] = [
  { host: 'factory.tailoredperfumes.com', brand: TAILORED },
  { host: 'factory.uniqueperfumes.es',    brand: UNIQ },
]

export function resolveBrand(hostname: string): Brand {
  const h = hostname.toLowerCase()
  // Igualdad o subdominio, nunca `includes`: `notfactory.tailoredperfumes.com`
  // no es Tailored, y un host que contenga el dominio dentro tampoco.
  const match = HOSTS.find(({ host }) => h === host || h.endsWith('.' + host))
  return match ? match.brand : AIRVAL
}

export const BRAND_OVERRIDE_KEY = 'brandOverride'

/** Pure brand resolution with a dev-only override. Testable (no window/DOM). */
export function resolveActiveBrand(opts: {
  hostname: string
  isDev: boolean
  search?: string
  override?: string | null
}): Brand {
  if (opts.isDev) {
    const fromUrl = new URLSearchParams(opts.search ?? '').get('brand')
    for (const candidate of [fromUrl, opts.override]) {
      if (candidate && Object.prototype.hasOwnProperty.call(BRANDS, candidate)) {
        return BRANDS[candidate as BrandId]
      }
    }
  }
  return resolveBrand(opts.hostname)
}

/** Thin wrapper reading window/localStorage/env; persists ?brand= to localStorage. Not unit-tested. */
export function getActiveBrand(): Brand {
  const isDev = import.meta.env.DEV
  const hasWindow = typeof window !== 'undefined'
  const search = hasWindow ? window.location.search : ''
  if (isDev && hasWindow) {
    const fromUrl = new URLSearchParams(search).get('brand')
    if (fromUrl && Object.prototype.hasOwnProperty.call(BRANDS, fromUrl)) localStorage.setItem(BRAND_OVERRIDE_KEY, fromUrl)
  }
  const override = isDev && hasWindow ? localStorage.getItem(BRAND_OVERRIDE_KEY) : null
  const hostname = hasWindow ? window.location.hostname : ''
  return resolveActiveBrand({ hostname, isDev, search, override })
}
